import { HttpClient, HttpErrorResponse, HttpEventType, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, lastValueFrom, of, throwError, timer, TimeoutError } from 'rxjs';
import { catchError, delay, retry, tap, timeout } from 'rxjs/operators';
import { environment } from '../../../../environments/environment';
import { ErrorService } from '../Error/error.service';
import { Platform } from '@ionic/angular';
import { Network } from '@capacitor/network';
import { Device } from '@capacitor/device';
import packageInfo from '../../../../../package.json';
import { UploadPolicyRejectedError } from 'src/app/errors/custom';
import { classifyS3Failure } from 'src/app/utils/S3UploadError';
import type { RequestFailure } from 'src/app/utils/FinalizeClose';

@Injectable({
  providedIn: 'root'
})
export class ApiService {
  protected url = environment.GATEWAY_HOST

  /**
   * Subidas a URL externa (presigned S3): se aborta por INACTIVIDAD
   * (sin progreso durante este período), no por duración total.
   * Un timeout de duración total rompía archivos grandes o redes lentas
   * (ej. PDF 80 MB en 3G ≈ 15 min de subida legítima).
   */
  private static readonly UPLOAD_INACTIVITY_TIMEOUT_MS = 60_000;

  /**
   * Sobre este tamaño se reduce el reintento a 1: cada retry de un PUT
   * reinicia la subida completa desde cero (costoso en redes lentas).
   */
  private static readonly LARGE_FILE_THRESHOLD_BYTES = 10 * 1024 * 1024;

  // TODO: Ocupar axios
  constructor(protected http: HttpClient,
              private readonly errorService: ErrorService,
              private platform: Platform,
  ) {}

  // Devuelve el JWT almacenado en el localStorage
  getJwt(): string {
    return localStorage.getItem('jwt') || ''
  }

  async get(endpoint: string): Promise<Observable<any>> {
    const headers = await this.getHeaders();
    return this.http.get(`${this.url}/api/${endpoint}`, { headers }).pipe(
      catchError(error => {
        this.errorService.showErrorScreen();
        return throwError(error);
      })
    );
  }

  /**
   * GET que devuelve el status en vez de mandar el error a la pantalla de error, como hace `get()`.
   * Es para consultas que el llamador tolera que fallen, así que nunca lanza: `status` es `null` si
   * no hubo respuesta (red caída, timeout o un fallo armando las cabeceras).
   */
  async getWithStatus(endpoint: string, timeoutMs: number): Promise<{ status: number | null; body: unknown }> {
    try {
      const headers = await this.getHeaders();
      const response = await lastValueFrom(
        this.http.get(`${this.url}/api/${endpoint}`, { headers, observe: 'response' }).pipe(timeout(timeoutMs))
      );
      return { status: response.status, body: response.body };
    } catch (error) {
      if (error instanceof HttpErrorResponse && error.status !== 0) {
        return { status: error.status, body: error.error };
      }
      return { status: null, body: null };
    }
  }

  /**
   * El POST de `getWithStatus`, con las mismas reglas: devuelve el status, tiene plazo, no reintenta,
   * nunca lanza y nunca muestra la pantalla de error. `status` es `null` si no hubo respuesta.
   */
  async postWithStatus(endpoint: string, body: unknown, timeoutMs: number): Promise<{ status: number | null; body: unknown }> {
    try {
      const headers = await this.getHeaders();
      const response = await lastValueFrom(
        this.http.post(`${this.url}/api/${endpoint}`, body, { headers, observe: 'response' }).pipe(timeout(timeoutMs))
      );
      return { status: response.status, body: response.body };
    } catch (error) {
      if (error instanceof HttpErrorResponse && error.status !== 0) {
        return { status: error.status, body: error.error };
      }
      return { status: null, body: null };
    }
  }

  /**
   * Por defecto: 10 s por intento, y se reintenta lo que `isTransientError` acepta. `options` es
   * para una ruta que no puede seguir esa política: un timeout propio, o su propia regla.
   */
  async post(
    endpoint: string,
    body: any,
    maxRetries: number = 3,
    delayMs: number = 1000,
    options?: { timeoutMs?: number; retryWhen?: (failure: RequestFailure) => boolean },
  ): Promise<Observable<any>> {
    const headers = await this.getHeaders();
    const retryWhen = options?.retryWhen;
    const isRetryable = retryWhen
      ? (error: unknown) => retryWhen(ApiService.asRequestFailure(error))
      : ApiService.isTransientError;
    return this.http.post(`${this.url}/api/${endpoint}`, body, { headers }).pipe(
      timeout(options?.timeoutMs ?? 10000),
      retry({
        count: maxRetries,
        delay: (error, retryCount) => {
          // Lo que la regla no reintenta se propaga de inmediato, en vez de quemar red y cuota del
          // gateway. Por defecto es un 4xx (auth/validación), que nunca se arregla reintentando.
          if (!isRetryable(error)) {
            return throwError(() => error);
          }
          // Backoff exponencial (1s/2s/4s con delayMs=1000) + jitter, para
          // que N clientes que fallaron a la vez no reintenten sincronizados.
          const backoffMs = delayMs * Math.pow(2, retryCount - 1);
          const jitterMs = Math.floor(Math.random() * 300);
          console.warn(`POST ${endpoint} reintento #${retryCount}/${maxRetries} en ${backoffMs + jitterMs} ms`);
          return timer(backoffMs + jitterMs);
        }
      }),
      catchError(error => {
        return throwError(() => `Error al subir datos. URL: ${endpoint}, Error: ${error?.message ?? error}`);
      })
    );
  }

  private static asRequestFailure(error: unknown): RequestFailure {
    if (error instanceof TimeoutError) {
      return { status: null, timedOut: true };
    }
    if (error instanceof HttpErrorResponse) {
      return { status: error.status, timedOut: false };
    }
    return { status: null, timedOut: false };
  }

  /** Errores que vale la pena reintentar: red caída/abortada, timeout, o 5xx/429/408 del servidor. */
  private static isTransientError(error: unknown): boolean {
    if (error instanceof TimeoutError) {
      return true;
    }
    if (error instanceof HttpErrorResponse) {
      return (
        error.status === 0 ||
        error.status === 408 ||
        error.status === 429 ||
        error.status >= 500
      );
    }
    return false;
  }

  /**
   * Sube un binario a una URL externa (presigned URL de S3).
   * - Timeout por INACTIVIDAD (60 s sin progreso), no por duración total:
   *   permite subidas legítimas de minutos en redes lentas (3G / archivos grandes).
   * - Reintentos acotados: 1 solo retry para archivos > 10 MB.
   * - `onProgress` (opcional): porcentaje 0–100 para mostrar avance en UI.
   */
  async putExternal(
    url: string,
    body: any,
    maxRetries: number = 3,
    delayMs: number = 1000,
    onProgress?: (percent: number) => void
  ): Promise<Observable<any>> {
    const size = typeof body?.size === 'number' ? body.size : 0;
    const effectiveRetries =
      size > ApiService.LARGE_FILE_THRESHOLD_BYTES ? 1 : maxRetries;
    return this.http
      .put(url, body, { reportProgress: true, observe: 'events' })
      .pipe(
        tap((event) => {
          if (event.type === HttpEventType.UploadProgress && event.total) {
            const percent = Math.round((event.loaded / event.total) * 100);
            onProgress?.(percent);
          }
        }),
        // Cada evento de progreso reinicia el contador: solo aborta si la
        // subida queda detenida (sin progreso) durante el período completo.
        timeout({ each: ApiService.UPLOAD_INACTIVITY_TIMEOUT_MS }),
        retry({
          count: effectiveRetries,
          delay: (error, retryCount) => {
            console.warn(
              `PUT reintento #${retryCount}/${effectiveRetries} (${Math.round(size / 1024)} KB)...`
            );
            return of(null).pipe(delay(delayMs));
          }
        }),
        catchError(error => throwError(() => this.asUploadFailure(error, url)))
      );
  }

  /**
   * Sube por **presigned POST multipart**, que es lo que entrega el Storage Manager cuando la firma
   * sale en esa modalidad.
   *
   * Conserva todo lo del PUT —timeout por inactividad, presupuesto de reintentos y progreso— porque
   * el criterio de la historia es que la experiencia del usuario no cambie.
   *
   * El orden de los campos no es estético: **el archivo va último**. S3 ignora todo lo que venga
   * después del campo `file`, así que una condición de la policy enviada detrás de él no se evalúa y
   * la subida se rechaza.
   */
  async postExternal(
    url: string,
    fields: Record<string, string>,
    body: Blob,
    maxRetries: number = 3,
    delayMs: number = 1000,
    onProgress?: (percent: number) => void
  ): Promise<Observable<any>> {
    const size = typeof body?.size === 'number' ? body.size : 0;
    const effectiveRetries =
      size > ApiService.LARGE_FILE_THRESHOLD_BYTES ? 1 : maxRetries;

    const form = new FormData();
    for (const [key, value] of Object.entries(fields)) {
      form.append(key, value);
    }
    form.append('file', body);

    return this.http
      .post(url, form, { reportProgress: true, observe: 'events' })
      .pipe(
        tap((event) => {
          if (event.type === HttpEventType.UploadProgress && event.total) {
            const percent = Math.round((event.loaded / event.total) * 100);
            onProgress?.(percent);
          }
        }),
        timeout({ each: ApiService.UPLOAD_INACTIVITY_TIMEOUT_MS }),
        retry({
          count: effectiveRetries,
          delay: (error, retryCount) => {
            // Un rechazo de la policy no mejora reintentando: la condición está en la firma y el
            // mismo archivo la vuelve a incumplir. Reintentarlo gasta datos del asegurado en algo
            // condenado y demora el mensaje que sí le sirve.
            const failure = this.asUploadFailure(error, url);
            if (failure instanceof UploadPolicyRejectedError) {
              return throwError(() => error);
            }
            console.warn(
              `POST reintento #${retryCount}/${effectiveRetries} (${Math.round(size / 1024)} KB)...`
            );
            return of(null).pipe(delay(delayMs));
          }
        }),
        catchError(error => throwError(() => this.asUploadFailure(error, url)))
      );
  }

  /**
   * Traduce el fallo de una subida en algo que el llamador pueda clasificar.
   *
   * Antes esto devolvía un **string**, y con él se perdían el status y el cuerpo de S3 — o sea que
   * río abajo era imposible distinguir un rechazo de policy de una caída de red, que es justo la
   * distinción que decide si la foto se encola o se le explica al usuario.
   */
  private asUploadFailure(error: unknown, url: string): Error {
    if (error instanceof HttpErrorResponse) {
      const verdict = classifyS3Failure(error.status, error.error);
      if (verdict.kind === 'permanent') {
        return new UploadPolicyRejectedError(verdict.userMessage);
      }
      return new Error(
        `Error al subir datos a url externa. URL: ${url}, status: ${error.status}` +
        (verdict.code ? `, code: ${verdict.code}` : '')
      );
    }
    const detail = error instanceof Error ? error.message : String(error);
    return new Error(`Error al subir datos a url externa. URL: ${url}, Error: ${detail}`);
  }

  async getHeaders(json: boolean = true): Promise<HttpHeaders> {
    const internetProvider = await Network.getStatus()
    const deviceInfo = await Device.getInfo();
    const headers = new HttpHeaders()
    .set('Authorization', `Bearer ${this.getJwt()}`)
    .set('X-App-Name',     packageInfo.name     ?? 'LET Autoinspección PWA')
    .set('X-App-Version',  packageInfo.version  ?? '1.0.0')
    .set('X-Internet-Provider', internetProvider.connectionType  ?? 'unknown')
    .set('X-Phone-Brand',  deviceInfo.manufacturer ?? 'unknown')
    .set('X-Phone-Model',  deviceInfo.model     ?? 'unknown')
    .set('X-Phone-So',     deviceInfo.operatingSystem ?? 'unknown')
    .set('X-Platform',     deviceInfo.platform  ?? 'unknown');

    // headers.keys().forEach(key => {
    //   console.log(`Header: ${key} = ${headers.get(key)}`);  
    // });

    if (json) {
      headers.set('Content-Type', 'application/json');
    }
    return headers;
  }

}
